// =============================================================================
// SIREPPAR - Encabezado editable de los informes (Secretario / TEP)
// =============================================================================
// El destinatario ("Al Sr. Secretario Electoral") y el nombre del partido viven
// DENTRO del cuerpo del informe, no en el generador del PDF.
//
// Antes el PDF imprimía `Partido: X` de forma fija, tomándolo del expediente: se
// veía en el archivo descargado pero NO en el modal, así que no había manera de
// corregirlo (por ejemplo si el partido cambió de denominación durante el trámite,
// o si hay que aclarar algo al lado del nombre). Ahora forma parte del texto
// editable y el PDF solo lo imprime cuando el informe NO trae cuerpo redactado
// (ver pdfGenerator: `if (data.denominacionPartido && !data.cuerpo)`).
//
// Cada destinatario puede ocupar VARIOS renglones (el Secretario lleva su nombre
// debajo del cargo). Por eso el mapa guarda arrays y no strings: la regex de
// idempotencia se arma sobre todos los renglones posibles, así reconoce —y
// reemplaza— un encabezado ya puesto en lugar de apilar otro.
// =============================================================================

export type DestinatarioInforme = 'SECRETARIO' | 'TEP' | 'SEP';

const DESTINATARIOS: Record<DestinatarioInforme, string[]> = {
  // Dos renglones: el cargo y la persona que lo ocupa.
  SECRETARIO: ['Al Sr. Secretario Electoral', 'Dr. Alejandro Tullio'],
  TEP: ['Al Tribunal Electoral Provincial'],
  // Devoluciones del Secretario: el destinatario es la SEP (la supervisora), que
  // tiene que retomar el expediente.
  SEP: ['A la Secretaría Electoral Permanente'],
};

// Todos los renglones de encabezado que el sistema puede haber escrito alguna vez.
// Se usan para reconocerlos y poder reemplazarlos.
const RENGLONES_CONOCIDOS = Object.values(DESTINATARIOS).flat();

function escaparRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Reconoce un encabezado ya puesto, para poder reemplazarlo en lugar de apilar
// otro. Hace falta porque los modales reaplican el armado del cuerpo sobre el texto
// actual (p. ej. al abrirse o al cambiar el bloque de advertencia documental), y sin
// esto el encabezado se duplicaba en cada pasada.
//
// El renglón del destinatario se busca con `*` (cero o más): así también limpia los
// encabezados de dos renglones que escribe SECRETARIO, y los de un solo renglón que
// quedaron guardados antes de que el Secretario llevara su nombre.
const RE_ENCABEZADO = new RegExp(
  `^(?:\\s*\\*\\*(?:${RENGLONES_CONOCIDOS.map(escaparRegex).join('|')})\\.\\*\\*\\s*\\n+)*` +
  `(?:\\s*\\*\\*(?:Partido|Alianza|Confederación|Agrupación):[^\\n]*\\*\\*\\s*\\n+)?`,
);

/** Quita el encabezado (destinatario + partido) si el texto ya lo tenía. */
export function sinEncabezadoInforme(texto: string): string {
  return texto.replace(RE_ENCABEZADO, '');
}

/**
 * Antepone el encabezado editable (destinatario + partido) al cuerpo del informe.
 * Es IDEMPOTENTE: si el texto ya traía encabezado, lo reemplaza.
 *
 * @param texto        Cuerpo redactado del informe.
 * @param opts.destinatario  Quién lo recibe. Se omite el renglón si no se indica.
 * @param opts.nombrePartido Denominación del partido. Se omite el renglón si falta.
 */
export function conEncabezadoInforme(
  texto: string,
  opts: {
    destinatario?: DestinatarioInforme;
    nombrePartido?: string | null;
    /**
     * Cómo se nombra a la agrupación en el encabezado. Por defecto "Partido", que
     * es lo que corresponde en casi todos los trámites; una ALIANZA no es un
     * partido y el renglón es lo primero que se lee.
     */
    etiquetaAgrupacion?: string;
  },
): string {
  const cuerpo = sinEncabezadoInforme(texto);
  const renglones: string[] = [];
  if (opts.destinatario) {
    renglones.push(...DESTINATARIOS[opts.destinatario].map((r) => `**${r}.**`));
  }
  if (opts.nombrePartido && opts.nombrePartido.trim()) {
    renglones.push(`**${opts.etiquetaAgrupacion ?? 'Partido'}: ${opts.nombrePartido.trim()}**`);
  }
  if (renglones.length === 0) return cuerpo;
  return `${renglones.join('\n\n')}\n\n${cuerpo}`;
}
